import React, { useState, useEffect, useCallback } from "react";
import { Card, Tabs, Table, Button, Modal, Form, Input, Select, Radio, DatePicker, Empty, message, Tooltip } from "antd";
import { PlusOutlined, DeleteOutlined, EyeOutlined, EditOutlined } from "@ant-design/icons";
import moment from "moment";
import { CLIENT_ID } from "constants/ApiEndPointsConstants";
import { ContentSectionsFormList, buildNewContent, parseNewContent } from "./ContentSections";
import SelectAllMultiple from "./SelectAllMultiple";
import {
  getAllStateList,
  getToolboxEducationList,
  getToolboxTypesWithSubEducation,
  addMonthlyEducation,
  insertToolboxEducation,
  deleteToolboxEducation,
  getToolboxContent,
  getAllMemoListing,
  insertMemo,
  deleteMemo,
  getSopList,
  insertSop,
  deleteSop,
  getMhraList,
  insertMhra,
  deleteMhra,
  getMhraWithSubEducation,
  getCompetencyList,
  getCompetencyDetailsById,
  createCompetency,
  deleteCompetency,
  getEducationContent,
  getMemoContentForEdit,
  updateEducationContent,
  getCompetencyForEdit,
  updateCompetency,
} from "./educationApi";

// Edit: every Add modal below also edits an existing item (its id in
// `editId`): it loads the saved item into the same form and saves it back.
const stateIds = (value) => String(value || "").split(",").map((v) => Number(v.trim())).filter(Boolean);
const loaded = ({ error, result }) => (!error && result?.success ? result.result : null);
const savedOk = ({ error, result }) => !error && result?.success !== false;
const editButton = (onClick) => (
  <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={onClick} style={{ marginRight: 8 }} aria-label="Edit" /></Tooltip>
);

const { TabPane } = Tabs;
const { Option } = Select;
const { confirm } = Modal;

// Rebuilt against a direct re-read of the old app's actual component code
// (education/monthlyEducation, /toolboxEducation, /memo, /sop, /mhra,
// /competency + their .store.js files) after a first pass based on route/
// constant names alone got the filters and form fields wrong.
//
// Known gap across Monthly/Toolbox Education: the old app's role_id filter
// comes from a sessionStorage value set during its own autologin redirect
// (a query param on the incoming URL) — there's no equivalent source here
// since we exchange tokens directly rather than redirecting through a login
// page, so role_id is always omitted (the path segment is optional).
// Known gap: the old app's "View" opens an iframe of its own rendered
// content; replaced here with a plain read-only modal instead of embedding
// the old app.

// ---- Monthly Education ----
const MonthlyEducationAddModal = ({ visible, editId, states, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    if (!editId) return;
    (async () => {
      setLoading(true);
      const row = loaded(await getEducationContent(editId));
      setLoading(false);
      if (!row) { message.error("Could not load this education."); onClose(); return; }
      form.setFieldsValue({
        title: row.title,
        topic: row.topic,
        purpose: row.purpose,
        education_date: row.education_month && row.education_year ? moment(`${row.education_month} ${row.education_year}`, "MMMM YYYY") : undefined,
        state_id: stateIds(row.state_id),
        for_staff: String(row.for_staff ?? "1"),
        sections: parseNewContent(row.new_content, row.title),
      });
    })();
  }, [visible, editId, form, onClose]);

  const handleOk = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const { sections, education_date, ...rest } = values;
      const body = { ...rest, education_date: education_date.format("MM/YYYY"), new_content: buildNewContent(sections) };
      const response = editId ? await updateEducationContent(editId, body) : await addMonthlyEducation(body);
      if (!savedOk(response)) message.error(response.result?.message || "Failed to save.");
      else { message.success(editId ? "Education updated." : "Saved."); onSaved(); onClose(); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editId ? "Edit Monthly Education" : "Add New Monthly Education"} visible={visible} onCancel={onClose} onOk={handleOk} okText={editId ? "Update" : "OK"} okButtonProps={{ disabled: loading }} confirmLoading={saving} destroyOnClose width={900}>
      <Form form={form} layout="vertical">
        <Form.Item name="title" label="Title" rules={[{ required: true, message: "Please enter the title." }]}><Input /></Form.Item>
        <Form.Item name="topic" label="Topic" rules={[{ required: true, message: "Please enter the topic." }]}><Input /></Form.Item>
        <Form.Item name="purpose" label="Purpose" rules={[{ required: true, message: "Please enter the purpose." }]}><Input /></Form.Item>
        <Form.Item name="education_date" label="Education Month" rules={[{ required: true, message: "This field is required." }]}>
          <DatePicker picker="month" format="MM-YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="state_id" label="States" rules={[{ required: true, message: "Please select at least one state." }]}>
          <SelectAllMultiple maxTagCount={1} options={states.map((s) => ({ value: s.state_id ?? s.id, label: s.state_name ?? s.name }))} />
        </Form.Item>
        <Form.Item name="for_staff" label="For Staff" rules={[{ required: true, message: "This field is required." }]}>
          <Radio.Group><Radio value="1">Yes</Radio><Radio value="0">No</Radio></Radio.Group>
        </Form.Item>
        <ContentSectionsFormList />
      </Form>
    </Modal>
  );
};

const ContentViewModal = ({ visible, content, onClose }) => (
  <Modal title={content?.title} visible={visible} onCancel={onClose} footer={null} destroyOnClose width={640}>
    <div style={{ color: "#5B6472", marginBottom: 12 }}>{content?.topic}</div>
    <div dangerouslySetInnerHTML={{ __html: content?.new_content || "" }} />
  </Modal>
);

export const MonthlyEducationTab = ({ states }) => {
  const [stateId, setStateId] = useState(undefined);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ visible: false, editId: null });
  const [viewContent, setViewContent] = useState(null);

  const load = useCallback(async (sid) => {
    if (!sid) { setRows([]); return; }
    setLoading(true);
    const { error, result } = await getToolboxEducationList(2, 0, sid);
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, []);

  const onStateChange = (value) => { setStateId(value); load(value); };
  const closeModal = useCallback(() => setModal({ visible: false, editId: null }), []);

  const handleView = async (row) => {
    const { error, result } = await getToolboxContent(row.id);
    if (error) { message.error("Could not load content."); return; }
    setViewContent(result?.result?.[0] || row);
  };

  const handleDelete = (row) => {
    confirm({
      title: "Delete education",
      content: `Delete "${row.title}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await deleteToolboxEducation(row.id);
        if (error) message.error(result?.message || "Failed to delete.");
        else { message.success("Deleted."); load(stateId); }
      },
    });
  };

  const columns = [
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Topic", dataIndex: "topic", key: "topic" },
    {
      title: "Action",
      key: "action",
      render: (_, row) => (
        <>
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} onClick={() => handleView(row)} style={{ marginRight: 8 }} aria-label="View" /></Tooltip>
          {editButton(() => setModal({ visible: true, editId: row.id }))}
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} aria-label="Delete" /></Tooltip>
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16 }}>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Select State</div>
          <Select allowClear showSearch optionFilterProp="children" style={{ width: 220 }} value={stateId} onChange={onStateChange}>
            {states.map((s) => <Option key={s.state_id ?? s.id} value={s.state_id ?? s.id}>{s.state_name ?? s.name}</Option>)}
          </Select>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal({ visible: true, editId: null })}>Add New Education</Button>
      </div>
      {!stateId ? (
        <Empty description="Please Select State." />
      ) : (
        <Table rowKey={(r, i) => r.id ?? i} columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 20 }} />
      )}
      <MonthlyEducationAddModal visible={modal.visible} editId={modal.editId} states={states} onClose={closeModal} onSaved={() => load(stateId)} />
      <ContentViewModal visible={!!viewContent} content={viewContent} onClose={() => setViewContent(null)} />
    </div>
  );
};

// ---- Toolbox Education ----
const ToolboxEducationAddModal = ({ visible, editId, states, toolboxGroups, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subOptions, setSubOptions] = useState([]);

  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    setSubOptions([]);
    if (!editId) return;
    (async () => {
      setLoading(true);
      const row = loaded(await getEducationContent(editId));
      setLoading(false);
      if (!row) { message.error("Could not load this toolbox education."); onClose(); return; }
      const group = toolboxGroups.find((g) => (g.sub_arr || []).some((sub) => sub.id === row.sub_toolbox_id));
      setSubOptions(group?.sub_arr || []);
      form.setFieldsValue({
        title: row.title,
        topic: row.topic,
        purpose: row.purpose,
        state_id: stateIds(row.state_id),
        education_type_id: group?.id,
        sub_toolbox_id: group ? row.sub_toolbox_id : undefined,
        for_staff: String(row.for_staff ?? "1"),
        sections: parseNewContent(row.new_content, row.title),
      });
    })();
  }, [visible, editId, toolboxGroups, form, onClose]);

  const onGroupChange = (groupId) => {
    form.setFieldsValue({ sub_toolbox_id: undefined });
    setSubOptions(toolboxGroups.find((g) => g.id === groupId)?.sub_arr || []);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const { sections, education_type_id, ...rest } = values;
      const body = { ...rest, new_content: buildNewContent(sections) };
      const response = editId ? await updateEducationContent(editId, body) : await insertToolboxEducation(body);
      if (!savedOk(response)) message.error(response.result?.message || "Failed to save.");
      else { message.success(editId ? "Toolbox education updated." : "Saved."); onSaved(); onClose(); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editId ? "Edit Toolbox Education" : "Add New Toolbox Education"} visible={visible} onCancel={onClose} onOk={handleOk} okText={editId ? "Update" : "OK"} okButtonProps={{ disabled: loading }} confirmLoading={saving} destroyOnClose width={900}>
      <Form form={form} layout="vertical">
        <Form.Item name="title" label="Title" rules={[{ required: true, message: "Please enter the title." }]}><Input /></Form.Item>
        <Form.Item name="topic" label="Topic" rules={[{ required: true, message: "Please enter the topic." }]}><Input /></Form.Item>
        <Form.Item name="purpose" label="Purpose" rules={[{ required: true, message: "Please enter the purpose." }]}><Input /></Form.Item>
        <Form.Item name="state_id" label="States" rules={[{ required: true, message: "Please select at least one state." }]}>
          <SelectAllMultiple maxTagCount={1} options={states.map((s) => ({ value: s.state_id ?? s.id, label: s.state_name ?? s.name }))} />
        </Form.Item>
        <Form.Item name="education_type_id" label="Toolbox Type" rules={[{ required: true, message: "Please select a toolbox type." }]}>
          <Select showSearch optionFilterProp="children" onChange={onGroupChange}>
            {toolboxGroups.map((g) => <Option key={g.id} value={g.id}>{g.name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="sub_toolbox_id" label="Sub Toolbox" rules={[{ required: true, message: "Please select a sub-toolbox." }]}>
          <Select showSearch optionFilterProp="children" disabled={!subOptions.length}>
            {subOptions.map((s) => <Option key={s.id} value={s.id}>{s.name}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item name="for_staff" label="For Staff" rules={[{ required: true, message: "This field is required." }]}>
          <Radio.Group><Radio value="1">Yes</Radio><Radio value="0">No</Radio></Radio.Group>
        </Form.Item>
        <ContentSectionsFormList />
      </Form>
    </Modal>
  );
};

export const ToolboxEducationTab = ({ states }) => {
  const [toolboxGroups, setToolboxGroups] = useState([]);
  const [subTypeId, setSubTypeId] = useState(undefined);
  const [stateId, setStateId] = useState(undefined);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ visible: false, editId: null });
  const [viewContent, setViewContent] = useState(null);
  const closeModal = useCallback(() => setModal({ visible: false, editId: null }), []);

  useEffect(() => {
    (async () => {
      const { error, result } = await getToolboxTypesWithSubEducation();
      setToolboxGroups(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, []);

  // The old app's Toolbox Education tab (unlike Registers -> Toolbox) only
  // ever offers the FIRST toolbox group's sub-items in its filter dropdown —
  // confirmed by reading toolboxEducation/index.js directly.
  const subOptions = toolboxGroups[0]?.sub_arr || [];

  const search = useCallback(async () => {
    if (!subTypeId || !stateId) {
      message.error("Please select a sub-toolbox and state first.");
      return;
    }
    setLoading(true);
    const { error, result } = await getToolboxEducationList(1, subTypeId, stateId);
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, [subTypeId, stateId]);

  const handleView = async (row) => {
    const { error, result } = await getToolboxContent(row.id);
    if (error) { message.error("Could not load content."); return; }
    setViewContent(result?.result?.[0] || row);
  };

  const handleDelete = (row) => {
    confirm({
      title: "Delete toolbox education",
      content: `Delete "${row.title}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await deleteToolboxEducation(row.id);
        if (error) message.error(result?.message || "Failed to delete.");
        else { message.success("Deleted."); search(); }
      },
    });
  };

  const columns = [
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Topic", dataIndex: "topic", key: "topic" },
    {
      title: "Action",
      key: "action",
      render: (_, row) => (
        <>
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} onClick={() => handleView(row)} style={{ marginRight: 8 }} aria-label="View" /></Tooltip>
          {editButton(() => setModal({ visible: true, editId: row.id }))}
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} aria-label="Delete" /></Tooltip>
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Select Sub Toolbox</div>
          <Select allowClear showSearch optionFilterProp="children" style={{ width: 220 }} value={subTypeId} onChange={setSubTypeId}>
            {subOptions.map((s) => <Option key={s.id} value={s.id}>{s.name}</Option>)}
          </Select>
        </div>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Select State</div>
          <Select allowClear showSearch optionFilterProp="children" style={{ width: 180 }} value={stateId} onChange={setStateId}>
            {states.map((s) => <Option key={s.state_id ?? s.id} value={s.state_id ?? s.id}>{s.state_name ?? s.name}</Option>)}
          </Select>
        </div>
        <Button type="primary" loading={loading} onClick={search}>Search</Button>
        <Button icon={<PlusOutlined />} onClick={() => setModal({ visible: true, editId: null })}>Add New Toolbox</Button>
      </div>
      <Table rowKey={(r, i) => r.id ?? i} columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 20 }} />
      <ToolboxEducationAddModal visible={modal.visible} editId={modal.editId} states={states} toolboxGroups={toolboxGroups} onClose={closeModal} onSaved={search} />
      <ContentViewModal visible={!!viewContent} content={viewContent} onClose={() => setViewContent(null)} />
    </div>
  );
};

// ---- Memo / SOP / MHRA (Memo+SOP share one shape; MHRA adds the
// education_type_id -> sub_toolbox_id cascade, same pattern as Toolbox
// Education above — the old app's own AddMhra.js always uses index 0's
// sub_arr regardless of the selected type, which reads as a latent bug there
// (selecting a different type doesn't change the sub-toolbox options); this
// rebuild cascades properly instead of reproducing that bug.) ----
const SimpleContentAddModal = ({ visible, title, editId, editLoader, states, hasPurpose, toolboxGroups, onClose, onSubmit, saving }) => {
  const [form] = Form.useForm();
  const [subOptions, setSubOptions] = useState([]);
  const [contentId, setContentId] = useState(null); // the toolbox_meeting row being edited
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    setSubOptions([]);
    setContentId(null);
    if (!editId) return;
    (async () => {
      setLoading(true);
      const row = loaded(await editLoader(editId));
      setLoading(false);
      if (!row) { message.error("Could not load this item."); onClose(); return; }
      const group = (toolboxGroups || []).find((g) => (g.sub_arr || []).some((sub) => sub.id === row.sub_toolbox_id));
      setSubOptions(group?.sub_arr || []);
      setContentId(row.id);
      form.setFieldsValue({
        title: row.title,
        topic: row.topic,
        purpose: row.purpose,
        education_type_id: group?.id,
        sub_toolbox_id: group ? row.sub_toolbox_id : undefined,
        state_id: stateIds(row.state_id),
        for_staff: String(row.for_staff ?? "1"),
        sections: parseNewContent(row.new_content, row.title),
      });
    })();
  }, [visible, editId, editLoader, toolboxGroups, form, onClose]);

  const onGroupChange = (groupId) => {
    form.setFieldsValue({ sub_toolbox_id: undefined });
    setSubOptions((toolboxGroups || []).find((g) => g.id === groupId)?.sub_arr || []);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    const { sections, ...rest } = values;
    onSubmit({ ...rest, new_content: buildNewContent(sections) }, contentId);
  };

  const heading = editId ? title.replace(/^Add New /, "Edit ") : title;

  return (
    <Modal title={heading} visible={visible} onCancel={onClose} onOk={handleOk} okText={editId ? "Update" : "OK"} okButtonProps={{ disabled: loading }} confirmLoading={saving} destroyOnClose width={900}>
      <Form form={form} layout="vertical">
        <Form.Item name="title" label="Title" rules={[{ required: true, message: "Please enter the title." }]}><Input /></Form.Item>
        <Form.Item name="topic" label="Topic" rules={[{ required: true, message: "Please enter the topic." }]}><Input /></Form.Item>
        {hasPurpose && <Form.Item name="purpose" label="Purpose" rules={[{ required: true, message: "Please enter the purpose." }]}><Input /></Form.Item>}
        {toolboxGroups && (
          <>
            <Form.Item name="education_type_id" label="Type" rules={[{ required: true, message: "Please select a type." }]}>
              <Select showSearch optionFilterProp="children" onChange={onGroupChange}>
                {toolboxGroups.map((g) => <Option key={g.id} value={g.id}>{g.name}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="sub_toolbox_id" label="Sub Toolbox" rules={[{ required: true, message: "Please select a sub-toolbox." }]}>
              <Select showSearch optionFilterProp="children" disabled={!subOptions.length}>
                {subOptions.map((s) => <Option key={s.id} value={s.id}>{s.name}</Option>)}
              </Select>
            </Form.Item>
          </>
        )}
        <Form.Item name="state_id" label="States" rules={[{ required: true, message: "Please select at least one state." }]}>
          <SelectAllMultiple maxTagCount={1} options={states.map((s) => ({ value: s.state_id ?? s.id, label: s.state_name ?? s.name }))} />
        </Form.Item>
        <Form.Item name="for_staff" label="For Staff" rules={[{ required: true, message: "This field is required." }]}>
          <Radio.Group><Radio value="1">Yes</Radio><Radio value="0">No</Radio></Radio.Group>
        </Form.Item>
        <ContentSectionsFormList />
      </Form>
    </Modal>
  );
};

// editLoader: loads the saved item for Edit from a list row's id (a memo is
// listed by its memo id, the others by their content id)
const SimpleContentTab = ({ titleField, columnTitle, listFn, insertFn, deleteFn, editLoader = getEducationContent, states, modalTitle, hasPurpose, toolboxGroupsFetcher }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ visible: false, editId: null });
  const [saving, setSaving] = useState(false);
  const [toolboxGroups, setToolboxGroups] = useState(null);
  const closeModal = useCallback(() => setModal({ visible: false, editId: null }), []);

  const load = useCallback(async () => {
    setLoading(true);
    const { error, result } = await listFn();
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, [listFn]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!toolboxGroupsFetcher) return;
    (async () => {
      const { error, result } = await toolboxGroupsFetcher();
      setToolboxGroups(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, [toolboxGroupsFetcher]);

  const handleSubmit = async (values, contentId) => {
    setSaving(true);
    try {
      const response = modal.editId ? await updateEducationContent(contentId, values) : await insertFn(values);
      if (!savedOk(response)) message.error(response.result?.message || "Failed to save.");
      else { message.success(modal.editId ? "Updated." : "Saved."); closeModal(); load(); }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (row) => {
    confirm({
      title: "Delete",
      content: `Delete "${row[titleField]}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await deleteFn(row.id);
        if (error) message.error(result?.message || "Failed to delete.");
        else { message.success("Deleted."); load(); }
      },
    });
  };

  const columns = [
    { title: columnTitle, dataIndex: titleField, key: titleField },
    {
      title: "Action",
      key: "action",
      render: (_, row) => (
        <>
          {editButton(() => setModal({ visible: true, editId: row.id }))}
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} aria-label="Delete" /></Tooltip>
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ textAlign: "right", marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal({ visible: true, editId: null })}>{modalTitle}</Button>
      </div>
      <Table rowKey={(r, i) => r.id ?? i} columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 20 }} />
      <SimpleContentAddModal
        visible={modal.visible}
        title={modalTitle}
        editId={modal.editId}
        editLoader={editLoader}
        states={states}
        hasPurpose={hasPurpose}
        toolboxGroups={toolboxGroups}
        onClose={closeModal}
        onSubmit={handleSubmit}
        saving={saving}
      />
    </div>
  );
};

// ---- Competency ----
const createCompetencyAdapter = (values) => {
  const { sections, ...rest } = values;
  return createCompetency({ ...rest, client_id: localStorage.getItem(CLIENT_ID), name: rest.title });
};

const CompetencyAddModal = ({ visible, editId, states, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    if (!editId) return;
    (async () => {
      setLoading(true);
      const row = loaded(await getCompetencyForEdit(editId));
      setLoading(false);
      if (!row) { message.error("Could not load this competency."); onClose(); return; }
      form.setFieldsValue({
        title: row.title,
        topic: row.topic,
        state_id: stateIds(row.state_id),
        for_staff: Number(row.for_staff) === 0 ? "no" : "yes",
        intro_content: row.intro_content,
        video_link: row.video_link,
        video_link2: row.video_link2,
        pdf_link: row.pdf_link,
        assessment_details: (row.assessment_details || []).map((a) => ({ assessment: a.assessment, criteria: a.criteria })),
      });
    })();
  }, [visible, editId, form, onClose]);

  const handleOk = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const body = { ...values, for_staff: values.for_staff === "yes" ? 1 : 0, state_id: (values.state_id || []).join(",") };
      const response = editId ? await updateCompetency(editId, body) : await createCompetencyAdapter(body);
      if (!savedOk(response)) message.error(response.result?.message || "Failed to save.");
      else { message.success(editId ? "Competency updated." : "Saved."); onSaved(); onClose(); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editId ? "Edit Competency" : "Add New Competency"} visible={visible} onCancel={onClose} onOk={handleOk} okText={editId ? "Update" : "OK"} okButtonProps={{ disabled: loading }} confirmLoading={saving} destroyOnClose width={640}>
      <Form form={form} layout="vertical">
        <Form.Item name="title" label="Title" rules={[{ required: true, message: "Please enter the title." }]}><Input /></Form.Item>
        <Form.Item name="topic" label="Topic" rules={[{ required: true, message: "Please enter the topic." }]}><Input /></Form.Item>
        <Form.Item name="state_id" label="States" rules={[{ required: true, message: "Please select at least one state." }]}>
          <SelectAllMultiple maxTagCount={2} options={states.map((s) => ({ value: s.state_id ?? s.id, label: s.state_name ?? s.name }))} />
        </Form.Item>
        <Form.Item name="for_staff" label="For Staff" rules={[{ required: true, message: "This field is required." }]}>
          <Select><Option value="yes">Yes</Option><Option value="no">No</Option></Select>
        </Form.Item>
        <Form.Item name="intro_content" label="Intro Content"><Input.TextArea rows={3} /></Form.Item>
        <Form.Item name="video_link" label="Video Link"><Input placeholder="https://..." /></Form.Item>
        <Form.Item name="video_link2" label="Video Link 2"><Input placeholder="https://..." /></Form.Item>
        <Form.Item name="pdf_link" label="PDF Link"><Input placeholder="https://..." /></Form.Item>
        <Form.List name="assessment_details">
          {(fields, { add, remove }) => (
            <>
              <div style={{ marginBottom: 8, fontSize: 13, color: "#5B6472" }}>Assessment Details</div>
              {fields.map(({ key, name, ...rest }) => (
                <Card key={key} size="small" style={{ marginBottom: 8 }}>
                  <Form.Item {...rest} name={[name, "assessment"]} label="Assessment" rules={[{ required: true, message: "This field is required." }]}><Input /></Form.Item>
                  <Form.Item {...rest} name={[name, "criteria"]} label="Criteria (Image URL or Description)" rules={[{ required: true, message: "This field is required." }]}><Input.TextArea rows={2} /></Form.Item>
                  <Button danger size="small" onClick={() => remove(name)}>Remove</Button>
                </Card>
              ))}
              <Button block icon={<PlusOutlined />} onClick={() => add({})}>Add Assessment Detail</Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export const CompetencyTab = ({ states }) => {
  const [stateId, setStateId] = useState(undefined);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ visible: false, editId: null });
  const [viewDetail, setViewDetail] = useState(null);
  const closeModal = useCallback(() => setModal({ visible: false, editId: null }), []);

  const load = useCallback(async (sid) => {
    setLoading(true);
    const { error, result } = await getCompetencyList(sid);
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, []);

  useEffect(() => { load(stateId); }, [load, stateId]);

  const handleView = async (row) => {
    const { error, result } = await getCompetencyDetailsById(row.competency_id);
    if (error) { message.error("Could not load competency details."); return; }
    setViewDetail(result?.result?.[0] || row);
  };

  const handleDelete = (row) => {
    confirm({
      title: "Delete competency",
      content: `Delete "${row.title}"?`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await deleteCompetency(row.toolbox_meeting_id);
        if (error) message.error(result?.message || "Failed to delete.");
        else { message.success("Deleted."); load(stateId); }
      },
    });
  };

  const columns = [
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Topic", dataIndex: "topic", key: "topic" },
    { title: "For Staff", dataIndex: "for_staff", key: "for_staff", render: (v) => (Number(v) === 1 ? "Yes" : "No") },
    {
      title: "Action",
      key: "action",
      render: (_, row) => (
        <>
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} onClick={() => handleView(row)} style={{ marginRight: 8 }} aria-label="View" /></Tooltip>
          {editButton(() => setModal({ visible: true, editId: row.toolbox_meeting_id }))}
          <Tooltip title="Delete"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} aria-label="Delete" /></Tooltip>
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16 }}>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Select State</div>
          <Select allowClear showSearch optionFilterProp="children" style={{ width: 220 }} value={stateId} onChange={setStateId}>
            {states.map((s) => <Option key={s.state_id ?? s.id} value={s.state_id ?? s.id}>{s.state_name ?? s.name}</Option>)}
          </Select>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModal({ visible: true, editId: null })}>Add New Competency</Button>
      </div>
      {!stateId ? (
        <Empty description="Please select a State to view competencies." />
      ) : (
        <Table rowKey="toolbox_meeting_id" columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 20 }} />
      )}
      <CompetencyAddModal visible={modal.visible} editId={modal.editId} states={states} onClose={closeModal} onSaved={() => load(stateId)} />
      <Modal title={viewDetail?.title} visible={!!viewDetail} onCancel={() => setViewDetail(null)} footer={null} destroyOnClose>
        <div style={{ color: "#5B6472", marginBottom: 8 }}>{viewDetail?.topic}</div>
        <div>{viewDetail?.intro_content}</div>
        {viewDetail?.video_link && <div style={{ marginTop: 8 }}><a href={viewDetail.video_link} target="_blank" rel="noreferrer">Video</a></div>}
        {viewDetail?.pdf_link && <div style={{ marginTop: 8 }}><a href={viewDetail.pdf_link} target="_blank" rel="noreferrer">PDF</a></div>}
        {(viewDetail?.assessment_details || []).map((a, i) => (
          <Card key={i} size="small" style={{ marginTop: 8 }}>
            <div style={{ fontWeight: 600 }}>{a.assessment}</div>
            <div>{a.criteria}</div>
          </Card>
        ))}
      </Modal>
    </div>
  );
};

// State list every Manage Education tab needs for its add forms.
export const useEducationStates = () => {
  const [states, setStates] = useState([]);
  useEffect(() => {
    (async () => {
      const { error, result } = await getAllStateList();
      setStates(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, []);
  return states;
};

// Content tabs on their own, for Employee Connect's subject layout.
export const MemoContentTab = ({ states }) => (
  <SimpleContentTab titleField="memo_name" columnTitle="Memo Title" listFn={getAllMemoListing} insertFn={insertMemo} deleteFn={deleteMemo} editLoader={getMemoContentForEdit} states={states} modalTitle="Add New Memo" />
);
export const SopContentTab = ({ states }) => (
  <SimpleContentTab titleField="title" columnTitle="SOP Title" listFn={getSopList} insertFn={insertSop} deleteFn={deleteSop} states={states} modalTitle="Add New SOP" />
);
export const MhraContentTab = ({ states }) => (
  <SimpleContentTab titleField="title" columnTitle="MHRA Title" listFn={getMhraList} insertFn={insertMhra} deleteFn={deleteMhra} states={states} modalTitle="Add New MHRA" hasPurpose toolboxGroupsFetcher={getMhraWithSubEducation} />
);

const ManageEducation = () => {
  const states = useEducationStates();

  return (
    <Card bodyStyle={{ padding: "24px 28px" }}>
      <Tabs defaultActiveKey="monthly">
        <TabPane tab="Education" key="monthly">
          <MonthlyEducationTab states={states} />
        </TabPane>
        <TabPane tab="Toolbox Education" key="toolbox">
          <ToolboxEducationTab states={states} />
        </TabPane>
        <TabPane tab="Memo" key="memo">
          <SimpleContentTab titleField="memo_name" columnTitle="Memo Title" listFn={getAllMemoListing} insertFn={insertMemo} deleteFn={deleteMemo} editLoader={getMemoContentForEdit} states={states} modalTitle="Add New Memo" />
        </TabPane>
        <TabPane tab="SOP" key="sop">
          <SimpleContentTab titleField="title" columnTitle="SOP Title" listFn={getSopList} insertFn={insertSop} deleteFn={deleteSop} states={states} modalTitle="Add New SOP" />
        </TabPane>
        <TabPane tab="MHRA" key="mhra">
          <SimpleContentTab
            titleField="title"
            columnTitle="MHRA Title"
            listFn={getMhraList}
            insertFn={insertMhra}
            deleteFn={deleteMhra}
            states={states}
            modalTitle="Add New MHRA"
            hasPurpose
            toolboxGroupsFetcher={getMhraWithSubEducation}
          />
        </TabPane>
        <TabPane tab="Competency" key="competency">
          <CompetencyTab states={states} />
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default ManageEducation;
